import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Card,
  CardHeader,
  CardBody,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  HStack,
  Avatar,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SimpleGrid,
  Progress,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Flex,
  Spacer,
  Icon,
  Center,
  Spinner,
  Container,
  useToast,
} from '@chakra-ui/react';
import { FiSearch, FiDownload, FiEye, FiMoreVertical, FiUser, FiMessageSquare, FiCalendar } from 'react-icons/fi';

const Transcript = ({ apiKey }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cardBg = useColorModeValue('white', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');
  const [timeRange, setTimeRange] = useState('7d');
  const [transcripts, setTranscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const toast = useToast();

  // Define a consistent color scheme
  const colors = {
    primary: useColorModeValue('blue.500', 'blue.300'),
    secondary: useColorModeValue('purple.500', 'purple.300'),
    background: useColorModeValue('white', 'gray.800'),
    cardBg: useColorModeValue('white', 'gray.700'),
    text: {
      primary: useColorModeValue('gray.800', 'white'),
      secondary: useColorModeValue('gray.600', 'gray.400'),
    },
    border: useColorModeValue('gray.200', 'gray.600'),
    userMessage: useColorModeValue('blue.50', 'blue.900'),
    botMessage: useColorModeValue('purple.50', 'purple.900'),
    hover: useColorModeValue('gray.50', 'gray.600'),
  };

  // Dummy data for transcripts
  const dummyTranscripts = [
    {
      id: 'conv-1',
      userId: 'user123',
      startTime: '2024-03-21T10:30:00Z',
      endTime: '2024-03-21T10:35:15Z',
      duration: '5m 15s',
      messages: [
        { id: 'msg-1', sender: 'user', text: 'Hi, I have a question about my order.', timestamp: '2024-03-21T10:30:05Z' },
        { id: 'msg-2', sender: 'bot', text: 'Hello! Please provide your order number.', timestamp: '2024-03-21T10:30:15Z' },
        { id: 'msg-3', sender: 'user', text: 'My order number is 12345.', timestamp: '2024-03-21T10:30:30Z' },
        { id: 'msg-4', sender: 'bot', text: 'Thank you. Let me look that up for you.', timestamp: '2024-03-21T10:30:45Z' },
        { id: 'msg-5', sender: 'bot', text: 'Your order #12345 was shipped on March 20th and is expected to arrive by March 25th.', timestamp: '2024-03-21T10:31:30Z' },
      ],
      status: 'completed',
    },
    {
      id: 'conv-2',
      userId: 'user456',
      startTime: '2024-03-21T09:15:00Z',
      endTime: '2024-03-21T09:20:00Z',
      duration: '5m 0s',
      messages: [
        { id: 'msg-6', sender: 'user', text: 'What are your return policies?', timestamp: '2024-03-21T09:15:05Z' },
        { id: 'msg-7', sender: 'bot', text: 'You can find our return policy on our website under the "Support" section.', timestamp: '2024-03-21T09:15:20Z' },
      ],
      status: 'completed',
    },
    {
      id: 'conv-3',
      userId: 'user789',
      startTime: '2024-03-21T08:00:00Z',
      endTime: null,
      duration: 'ongoing',
      messages: [
        { id: 'msg-8', sender: 'user', text: 'I need help setting up the product.', timestamp: '2024-03-21T08:00:05Z' },
        { id: 'msg-9', sender: 'bot', text: 'Sure, I can help with that. Which product do you have?', timestamp: '2024-03-21T08:00:20Z' },
      ],
      status: 'ongoing',
    },
  ];

  const filteredTranscripts = dummyTranscripts.filter(transcript =>
    transcript.userId.toLowerCase().includes(searchQuery.toLowerCase()) ||
    transcript.status.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleViewTranscript = (transcript) => {
    setSelectedTranscript(transcript);
    onOpen();
  };

  const handleDownloadTranscript = (transcriptId) => {
    // Handle download logic
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'completed':
        return 'green';
      case 'ongoing':
        return 'orange';
      default:
        return 'gray';
    }
  };

  // Dummy data for transcription
  const transcriptionData = {
    totalTranscriptions: 2500,
    accuracy: '95%',
    averageDuration: '2.5 min',
    languages: ['English', 'Spanish', 'French', 'German'],
    recentTranscriptions: [
      {
        id: 1,
        filename: 'meeting_20240321.mp3',
        duration: '15:30',
        language: 'English',
        status: 'completed',
        accuracy: '98%',
        timestamp: '2024-03-21T10:30:00',
      },
      {
        id: 2,
        filename: 'interview_20240320.mp3',
        duration: '45:15',
        language: 'Spanish',
        status: 'completed',
        accuracy: '96%',
        timestamp: '2024-03-20T15:45:00',
      },
      {
        id: 3,
        filename: 'conference_20240319.mp3',
        duration: '120:00',
        language: 'English',
        status: 'processing',
        accuracy: 'pending',
        timestamp: '2024-03-19T09:00:00',
      },
    ],
  };

  useEffect(() => {
    const fetchTranscripts = async () => {
      try {
        if (apiKey) {
          // In a real application, use the apiKey to fetch transcripts
          // from Voiceflow's Transcripts API
          console.log("Using apiKey to fetch transcripts:", apiKey);
          // Simulate API call delay
          await new Promise(resolve => setTimeout(resolve, 1000));
          setTranscripts(dummyTranscripts);
        } else {
          console.log("No API key provided, loading dummy transcripts.");
          setTranscripts(dummyTranscripts);
        }
      } catch (err) {
        toast({
          title: 'Error loading transcripts',
          description: err.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTranscripts();
  }, [apiKey, toast]);

  if (loading) {
    return (
      <Center minH="200px">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!transcripts || transcripts.length === 0) {
    return (
      <Center minH="200px">
        <Text fontSize="lg" color="gray.500">No transcripts available.</Text>
      </Center>
    );
  }

  return (
    <Box bg={colors.background} minH="100vh" p={6}>
      <VStack spacing={6} align="stretch">
        <Heading size="md" color={colors.primary}>
          Conversation Transcripts
        </Heading>

        <Container maxW="4xl" py={10}>
          <Heading size="lg" mb={6} color={colors.primary}>
            Voiceflow Conversation Transcripts
          </Heading>

          {loading ? (
            <Center>
              <Spinner size="xl" color={colors.primary} />
            </Center>
          ) : transcripts.length === 0 ? (
            <Center>
              <Text fontSize="lg" color={colors.text.secondary}>
                No transcripts found.
              </Text>
            </Center>
          ) : (
            <Accordion allowMultiple>
              {transcripts.map((transcript, index) => (
                <AccordionItem 
                  key={index} 
                  border="1px solid" 
                  borderColor={colors.border}
                  borderRadius="lg" 
                  mb={4}
                  _hover={{ borderColor: colors.primary }}
                >
                  <AccordionButton 
                    _expanded={{ 
                      bg: colors.userMessage,
                      color: colors.primary,
                      borderBottomRadius: 0
                    }}
                    _hover={{ bg: colors.hover }}
                  >
                    <Box flex="1" textAlign="left">
                      <Text fontWeight="bold" color={colors.text.primary}>
                        User: {transcript.userID || 'Unknown'}
                      </Text>
                      <Text fontSize="sm" color={colors.text.secondary}>
                        Started: {new Date(transcript.startTime).toLocaleString()}
                      </Text>
                    </Box>
                    <AccordionIcon color={colors.primary} />
                  </AccordionButton>
                  <AccordionPanel pb={4} bg={colors.cardBg}>
                    <VStack align="start" spacing={3}>
                      {transcript.messages?.map((msg, i) => (
                        <Box
                          key={i}
                          bg={msg.type === 'user' ? colors.userMessage : colors.botMessage}
                          p={4}
                          borderRadius="lg"
                          w="100%"
                          border="1px solid"
                          borderColor={colors.border}
                        >
                          <Text 
                            fontSize="sm" 
                            color={colors.text.secondary}
                            fontWeight="medium"
                          >
                            {msg.type === 'user' ? '👤 User' : '🤖 Bot'} @{' '}
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </Text>
                          <Text 
                            mt={2} 
                            color={colors.text.primary}
                          >
                            {msg.payload?.message || msg.text || '[No Message]'}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </Container>
      </VStack>

      {/* Transcript View Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent bg={colors.cardBg}>
          <ModalHeader color={colors.primary}>
            Conversation Transcript
          </ModalHeader>
          <ModalCloseButton color={colors.text.secondary} />
          <ModalBody pb={6}>
            {selectedTranscript && (
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontWeight="bold" color={colors.text.primary}>
                    User: {selectedTranscript.userId}
                  </Text>
                  <Text color={colors.text.secondary}>
                    Duration: {selectedTranscript.duration}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold" color={colors.text.primary}>
                    Status: {selectedTranscript.status}
                  </Text>
                  <Text color={colors.text.secondary}>
                    Messages: {selectedTranscript.messages.length}
                  </Text>
                </HStack>
                <Box
                  borderWidth={1}
                  borderColor={colors.border}
                  borderRadius="lg"
                  p={4}
                  maxH="400px"
                  overflowY="auto"
                  bg={colors.background}
                >
                  {selectedTranscript.messages.map((message, index) => (
                    <Box
                      key={index}
                      mb={4}
                      p={4}
                      bg={message.sender === 'user' ? colors.userMessage : colors.botMessage}
                      borderRadius="lg"
                      border="1px solid"
                      borderColor={colors.border}
                    >
                      <Text 
                        fontWeight="bold" 
                        mb={2}
                        color={colors.text.primary}
                      >
                        {message.sender === 'user' ? '👤 User' : '🤖 Bot'}:
                      </Text>
                      <Text color={colors.text.primary}>
                        {message.text}
                      </Text>
                    </Box>
                  ))}
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Transcript; 